/**
 * TanStack Query key factory. Keep keys hierarchical so that
 * `queryClient.invalidateQueries({ queryKey: keys.admin.all })` hits every admin query.
 */
export const keys = {
  all: /** @type {const} */ (['blimo']),
  me: () => /** @type {const} */ (['blimo', 'me']),
  twoFactor: {
    all: () => /** @type {const} */ (['blimo', 'me', 'two-factor']),
    qrCode: () => /** @type {const} */ (['blimo', 'me', 'two-factor', 'qr-code']),
    recoveryCodes: () => /** @type {const} */ (['blimo', 'me', 'two-factor', 'recovery-codes']),
  },
  admin: {
    all: /** @type {const} */ (['blimo', 'admin']),
    dashboard: () => /** @type {const} */ (['blimo', 'admin', 'dashboard']),
    /** @param {string} resource @param {Record<string, unknown>} [params] */
    list: (resource, params = {}) => /** @type {const} */ (['blimo', 'admin', resource, 'list', params]),
    /** @param {string} resource @param {string|number} id */
    detail: (resource, id) => /** @type {const} */ (['blimo', 'admin', resource, 'detail', String(id)]),
    /**
     * Nested collection of a record, e.g. `sub('properties', 5, 'ownerships', { current: true })`.
     * Lives below `detail(resource, id)` so invalidating the detail also refreshes its sub-lists.
     * @param {string} resource @param {string|number} id @param {string} name @param {Record<string, unknown>} [params]
     */
    sub: (resource, id, name, params = {}) => /** @type {const} */ (['blimo', 'admin', resource, 'detail', String(id), name, params]),
    roles: () => /** @type {const} */ (['blimo', 'admin', 'roles']),
    settings: () => /** @type {const} */ (['blimo', 'admin', 'settings']),
    /** Finance reports, e.g. `finance('open-items', { op_type: 'BK' })`. @param {string} [name] @param {Record<string, unknown>} [params] */
    finance: (name, params = {}) => /** @type {const} */ (name ? ['blimo', 'admin', 'finance', name, params] : ['blimo', 'admin', 'finance']),
    /** @param {string} q */
    search: (q) => /** @type {const} */ (['blimo', 'admin', 'search', q]),
    /** Everything of one resource (lists, details, sub-lists), e.g. for invalidation after a mutation. @param {string} resource */
    resource: (resource) => /** @type {const} */ (['blimo', 'admin', resource]),
  },
  portal: {
    all: /** @type {const} */ (['blimo', 'portal']),
    dashboard: () => /** @type {const} */ (['blimo', 'portal', 'dashboard']),
    home: () => /** @type {const} */ (['blimo', 'portal', 'home']),
    profile: () => /** @type {const} */ (['blimo', 'portal', 'profile']),
    /** @param {string} resource @param {Record<string, unknown>} [params] */
    list: (resource, params = {}) => /** @type {const} */ (['blimo', 'portal', resource, 'list', params]),
    /** @param {string} resource @param {string|number} id */
    detail: (resource, id) => /** @type {const} */ (['blimo', 'portal', resource, 'detail', String(id)]),
    /** @param {string} resource @param {string|number} id @param {string} name @param {Record<string, unknown>} [params] */
    sub: (resource, id, name, params) =>
      /** @type {const} */ (
        params ? ['blimo', 'portal', resource, 'detail', String(id), name, params] : ['blimo', 'portal', resource, 'detail', String(id), name]
      ),
    /** Everything of one resource (lists, details, sub-lists). @param {string} resource */
    resource: (resource) => /** @type {const} */ (['blimo', 'portal', resource]),
    /** Blimo AI, e.g. `assistant('status')`, `assistant('conversations')`, `assistant('conversation', 5)`; `assistant()` = all. @param {string} [name] @param {string|number} [id] */
    assistant: (name, id) =>
      /** @type {const} */ (
        name
          ? id !== undefined
            ? ['blimo', 'portal', 'assistant', name, String(id)]
            : ['blimo', 'portal', 'assistant', name]
          : ['blimo', 'portal', 'assistant']
      ),
    /** Finance views; `finance()` = all of them (invalidate after a baseline change). @param {string} [name] @param {Record<string, unknown>} [params] */
    finance: (name, params = {}) => /** @type {const} */ (name ? ['blimo', 'portal', 'finance', name, params] : ['blimo', 'portal', 'finance']),
  },
  notifications: () => /** @type {const} */ (['blimo', 'notifications']),
  /** Public branding (GET /branding) – outside `admin`/`portal` so logout (`client.clear()`) refetches it right away. */
  branding: () => /** @type {const} */ (['blimo', 'branding']),
};

/**
 * Query options for GET /me. 401/403 are not retried (they are answers, not failures).
 * @param {{ auth: import('./auth.js').AuthApi }} api
 */
export function meQueryOptions(api) {
  return {
    queryKey: keys.me(),
    queryFn: () => api.auth.me(),
    staleTime: 5 * 60 * 1000,
    retry: (/** @type {number} */ count, /** @type {any} */ error) => {
      const status = error?.status;
      if (status && status >= 400 && status < 500) return false;
      return count < 2;
    },
  };
}
